import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
  Snackbar,
  Grid,
  alpha,
  Tab,
  Tabs,
} from '@mui/material';
import {
  PhoneAndroid as AndroidIcon,
  Construction as CantiereIcon,
  FlightTakeoff as DroneIcon,
} from '@mui/icons-material';
import { ApkUploadZone } from '../components/appmobile/ApkUploadZone';
import { LocalApkDetector } from '../components/appmobile/LocalApkDetector';
import { ReleaseNotesForm } from '../components/appmobile/ReleaseNotesForm';
import { ActiveVersionCard } from '../components/appmobile/ActiveVersionCard';
import { indirizzoApk, paginaPerScaricare } from '@shared/app-telefono/appAndroid';
import { ReleaseHistoryTable } from '../components/appmobile/ReleaseHistoryTable';
import { ReleaseFrequencyChart } from '../components/appmobile/ReleaseFrequencyChart';
import {
  uploadApkToStagingForApp,
  parseApkMetadata,
  publishReleaseForApp,
  getActiveVersionForApp,
  getReleaseHistoryForApp,
  MOBILE_APPS,
} from '../services/appMobileService';
import type { MobileAppId } from '../services/appMobileService';
import MagazzinoIcon from '@mui/icons-material/Inventory2Outlined';
import type {
  ActiveVersion,
  AppRelease,
  ParseApkMetadataResponse,
  UploadState,
  ReleaseNotesDetailed,
} from '../types/AppMobileTypes';

export const AppMobilePage: React.FC = () => {
  const [currentApp, setCurrentApp] = useState<MobileAppId>('yumacall');
  const appConfig = MOBILE_APPS[currentApp];

  const [activeVersion, setActiveVersion] = useState<ActiveVersion | null>(null);
  const [releases, setReleases] = useState<AppRelease[]>([]);
  const [loadingVersion, setLoadingVersion] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [apkMetadata, setApkMetadata] = useState<ParseApkMetadataResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoadingVersion(true);
      setLoadingHistory(true);

      const [version, history] = await Promise.all([
        getActiveVersionForApp(currentApp),
        getReleaseHistoryForApp(currentApp),
      ]);

      setActiveVersion(version);
      setReleases(history);
    } catch (err: any) {
      console.error(`Errore caricamento dati ${appConfig.label}:`, err);
      setError('Errore nel caricamento dei dati');
    } finally {
      setLoadingVersion(false);
      setLoadingHistory(false);
    }
  }, [currentApp, appConfig.label]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAppChange = (_: React.SyntheticEvent, newValue: MobileAppId) => {
    setCurrentApp(newValue);
    setUploadState('idle');
    setUploadProgress(null);
    setApkMetadata(null);
    setError(null);
  };

  const handleFileSelected = useCallback(async (file: File) => {
    setError(null);
    setApkMetadata(null);
    setUploadState('uploading');
    setUploadProgress(0);

    try {
      const uploadTask = uploadApkToStagingForApp(file, currentApp);

      await new Promise<void>((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(progress);
          },
          (err) => {
            reject(err);
          },
          () => {
            resolve();
          }
        );
      });

      setUploadProgress(null);
      setUploadState('parsing');

      const metadata = await parseApkMetadata(
        appConfig.stagingPath,
        appConfig.finalApkPath,
        appConfig.packageName,
      );
      setApkMetadata(metadata);
      setUploadState('ready');
    } catch (err: any) {
      console.error('Errore upload/parsing APK:', err);
      setError(err.message || 'Errore durante il caricamento dell\'APK');
      setUploadState('error');
      setUploadProgress(null);
    }
  }, [currentApp, appConfig.stagingPath, appConfig.finalApkPath]);

  const handlePublish = useCallback(async (releaseNotes: ReleaseNotesDetailed, forceUpdate: boolean) => {
    if (!apkMetadata) return;

    setUploadState('publishing');
    setError(null);

    try {
      await publishReleaseForApp({
        versionCode: apkMetadata.versionCode,
        versionName: apkMetadata.versionName,
        packageName: apkMetadata.packageName,
        apkDownloadUrl: apkMetadata.downloadUrl,
        apkSizeBytes: apkMetadata.sizeBytes,
        apkStoragePath: appConfig.finalApkPath,
        forceUpdate,
        releaseNotesDetailed: releaseNotes,
      }, currentApp);

      setSuccessMessage(`Versione ${apkMetadata.versionName} pubblicata con successo!`);
      setUploadState('idle');
      setApkMetadata(null);

      await loadData();
    } catch (err: any) {
      console.error('Errore pubblicazione:', err);
      setError(err.message || 'Errore durante la pubblicazione');
      setUploadState('ready');
    }
  }, [apkMetadata, loadData, currentApp, appConfig.finalApkPath]);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 1.5, sm: 2, md: 3 }, pt: { xs: 2, sm: 3 }, pb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '12px',
            bgcolor: alpha('#4caf50', 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <AndroidIcon sx={{ fontSize: 26, color: '#4caf50' }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2 }}>
            Gestione app mobile
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Carica nuove versioni, gestisci le release notes e pubblica aggiornamenti
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={currentApp}
          onChange={handleAppChange}
          sx={{ minHeight: 42 }}
        >
          <Tab
            value="yumacall"
            label="YumaCALL"
            icon={<AndroidIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
            sx={{ minHeight: 42, textTransform: 'none' }}
          />
          <Tab
            value="yumawork"
            label="YumaWORK"
            icon={<CantiereIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
            sx={{ minHeight: 42, textTransform: 'none' }}
          />
          {/* ⚠️ È LA VARIANTE DEL GESTIONALE (com.yumaos.yumafly), non quella pubblica.
              L'app pubblica di YumaFLY vive nel suo progetto Firebase e si distribuisce dal suo
              sito: qui si gestisce solo quella che usano i tenant di YumaOS. Il pacchetto atteso
              è dichiarato in MOBILE_APPS e verificato dentro l'APK dalla Cloud Function, quindi
              pubblicare l'una sopra l'altra è impossibile, non solo improbabile. */}
          <Tab
            value="yumafly"
            label="YumaFLY"
            icon={<DroneIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
            sx={{ minHeight: 42, textTransform: 'none' }}
          />
          <Tab
            value="yumastock"
            label="YumaSTOCK"
            icon={<MagazzinoIcon sx={{ fontSize: 20 }} />}
            iconPosition="start"
            sx={{ minHeight: 42, textTransform: 'none' }}
          />
        </Tabs>
      </Box>

      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ mb: 2, borderRadius: 2 }}
        >
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <ReleaseFrequencyChart releases={releases} loading={loadingHistory} />
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Box sx={{ mb: 3 }}>
            <ActiveVersionCard version={activeVersion} loading={loadingVersion} paginaDaMandare={paginaPerScaricare(currentApp)} linkApk={indirizzoApk(currentApp)} />
          </Box>

          {(uploadState === 'idle' || uploadState === 'error') && (
            <Box sx={{ mb: 3 }}>
              <LocalApkDetector
                activeVersion={activeVersion}
                onFileReady={handleFileSelected}
                disabled={uploadState !== 'idle'}
                appLabel={appConfig.label}
                storageKey={`apk-directory-handle-${currentApp}`}
                apkFileName={appConfig.apkFileName}
              />
            </Box>
          )}

          {(uploadState === 'idle' || uploadState === 'uploading' || uploadState === 'error') && (
            <Box sx={{ mb: 3 }}>
              <ApkUploadZone
                onFileSelected={handleFileSelected}
                uploadProgress={uploadState === 'uploading' ? uploadProgress : null}
                disabled={uploadState !== 'idle'}
                activeVersion={activeVersion}
              />
            </Box>
          )}

          {uploadState === 'parsing' && (
            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
              Analisi dell'APK in corso... Estrazione metadata (versione, pacchetto)
            </Alert>
          )}

          {(uploadState === 'ready' || uploadState === 'publishing') && apkMetadata && (
            <Box sx={{ mb: 3 }}>
              <ReleaseNotesForm
                metadata={apkMetadata}
                onPublish={handlePublish}
                isPublishing={uploadState === 'publishing'}
              />
            </Box>
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <ReleaseHistoryTable releases={releases} loading={loadingHistory} />
        </Grid>
      </Grid>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ borderRadius: 2 }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};
